import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  progressApi,
  type ClientType, type LoginResult, type Mt5Status, type ProgressView,
} from '@/api/auth'
import { profileApi, type ProfileResult } from '@/api/profile'
import { blockKindOf, capsOf, isTerminal, type BlockKind, type Capability } from '@/constants/capabilities'
import { isCorporateType, type CustomerType } from '@/constants/dicts'

export const useAuthStore = defineStore('auth', () => {
  /**
   * 两份来源，都可能是门禁判定的依据：
   *
   *   session —— 登录响应（接口 2.0 起就带 portalMode / onboardingProgress）
   *   profile —— /profile/me，字段更全（手机、地址、证件有效期），个人资料页要用
   *
   * 有 session 就能立刻判门禁并渲染落地页，不必等 /profile/me 回来。
   * profile 到了以后以它为准（它是全模式可读的权威来源）。
   */
  const session = ref<LoginResult | null>(null)
  const profile = ref<ProfileResult | null>(null)
  const progress = ref<ProgressView | null>(null)

  /**
   * 距密码到期天数（R-18）。
   *
   * ✅ 接口 4.0 已把字段补回，且 `LoginResult` 与 `ProfileResult` 都有 ——
   * 所以刷新页面（session 丢了、token 还在）也拿得到，
   * 需求 §5.3 的 14 / 7 / 3 分级提醒完整可用。
   *
   * 仍然保留"取不到就退化"的写法：字段缺失时只是不做提前提醒，
   * 强制改密由接口 40301 兜底，功能不会坏。
   */
  const passwordExpireInDays = ref<number | null>(null)
  const mustChangePassword = ref(false)
  /** 本次会话内已弹过密码到期弹窗 / 已关掉黄条 —— 只活在内存里，重登即重置 */
  const pwdModalShown = ref(false)
  const pwdBannerDismissed = ref(false)
  /** 本次登录已自动弹过概览页任务弹窗（§6.1.2「首次进入首页」）—— 与上两个同样只活在内存里，重登即重置 */
  const tasksModalShown = ref(false)

  /**
   * 门禁判定已就绪（两份来源任一到位即可）。这同时就是「已登录」的唯一判据 ——
   * 原来另有一个 `loggedIn = computed(() => !!tokenStore.get())`：读的是 localStorage，没有任何响应式依赖，
   * 首次求值后永不更新；而 session / profile 只有登录成功才会写入、`reset()` 必清，推导出来的才是真的。
   */
  const ready = computed(() => !!profile.value || !!session.value)

  const mode = computed(
    () => profile.value?.portalMode ?? session.value?.portalMode ?? progress.value?.portalMode ?? 'NORMAL',
  )
  const stage = computed(
    () =>
      profile.value?.onboardingProgress ??
      session.value?.onboardingProgress ??
      progress.value?.progress ??
      'PENDING_SUBMIT',
  )

  /**
   * 注册邮箱（登录响应与档案同源）。顶栏用户菜单用它作副行（用户 09-18）。
   * 统一账号不在这里：它仅后台显示，后端 2026-09-15 起不再下发给客户端。
   */
  const email = computed(() => profile.value?.email ?? session.value?.email ?? null)
  const displayName = computed(
    () => profile.value?.fullName ?? session.value?.fullName ?? profile.value?.email ?? session.value?.email ?? '',
  )

  /** 开户是否完成（= 已开通 MT5）。客户可见标签只有「待完成开户 / 开户未通过」 */
  const onboarded = computed(() => stage.value === 'MT5_OPENED')
  /**
   * 首户开立状态（接口 0.8.4 accountOpening）—— 进度没到 MT5_OPENED、但也没有任何任务要客户做的两种情况：
   *   PROVISIONING：KYC 已通过、MT5 建户后台重做中 → 「账户配置中」；
   *   FAILED：CRM 人工确认不开、首开提案已取消 → 「未能开立，请联系客服」。
   * 此前两页只认 progress / KYC 记录，把客户在「继续身份验证 ↔ 返回」之间推来推去（dev 80000002）。
   */
  const accountOpening = computed(
    () => (onboarded.value ? null : (profile.value?.accountOpening ?? session.value?.accountOpening ?? null)),
  )
  const provisioning = computed(() => accountOpening.value === 'PROVISIONING')
  const openFailed = computed(() => accountOpening.value === 'FAILED')

  /**
   * 客户类型（接口 0.10.0）。整套分流挂在这上面：菜单、任务弹窗、公司信息页。
   * 取不到时**按零售兜底**——渲染成零售顶多少几个菜单，渲染成公司户则会去请求
   * 一批零售客户根本没有的端点（人员清单、公司信息），页面直接一片错误。
   */
  const clientType = computed<ClientType>(
    () => profile.value?.clientType ?? session.value?.clientType
      ?? (isCorporateType(profile.value?.customerType ?? session.value?.customerType) ? 'wholesale' : 'retail'),
  )
  const isWholesale = computed(() => clientType.value === 'wholesale')
  /** 客户类型四值（二期 P01）：只用于展示类型与判断零售保护，分流仍看 clientType */
  const customerType = computed<CustomerType | null>(() => profile.value?.customerType ?? session.value?.customerType ?? null)
  /**
   * 是否个人 Retail——负余额保护等零售保护只给它（个人 / 公司 Sophisticated 与公司 Wholesale 都不适用）。
   * 类型缺失（旧后端）时按分流兜底：个人版当零售，公司版不是
   */
  const isRetailClient = computed(() => (customerType.value ?? (isWholesale.value ? 'WHOLESALE' : 'RETAIL')) === 'RETAIL')

  /**
   * MT5 开通阶段（接口 0.10.0 mt5Status，优先于 0.8.4 的 accountOpening）。
   * 后端没给（旧版本）时由 accountOpening 映射回来：PROVISIONING→opening / FAILED→failed；
   * 已开户恒 opened。两者并存到后端全量升级为止——直接改用新字段会让没升级的环境整页显示「未开通」。
   */
  /** 后端原样给的 mt5Status（未经下面的核实），只给 `useActivationDone` 判断要不要去核实 */
  const mt5Reported = computed<Mt5Status | null>(
    () => profile.value?.mt5Status ?? session.value?.mt5Status ?? null,
  )
  /**
   * 激活任务（知识测试 + 身份认证；公司户＝全员认证）是否已完成。null＝尚未核实。
   * 由布局层的 `useActivationDone` 在需要时查 kyc/status 或 kyc/people 后写入；重登即清。
   */
  const activationDone = ref<boolean | null>(null)
  function setActivationDone(v: boolean | null) {
    activationDone.value = v
  }
  const mt5Phase = computed<Mt5Status | null>(() => {
    const explicit = mt5Reported.value
    if (onboarded.value) return 'opened'
    if (explicit === 'failed' || accountOpening.value === 'FAILED') return 'failed'
    if (explicit === 'opening' || accountOpening.value === 'PROVISIONING') {
      /**
       * ⚠️ 契约里 `opening`＝「有在途首开提案」，而首开提案在申请提交那一刻就存在——
       * 知识测试、身份认证还没做的客户后端也给 `opening`（UAT 2026-09-18 实测：KYC 转人工的客户首页显示「开通中」）。
       * 所以 `opening` 只在「客户已无事可做」时才算：accountOpening=PROVISIONING（KYC 已过、重做中）
       * 或经 kyc/status（公司户 kyc/people）核实激活任务已完成。未核实 / 未完成一律当 null → 页面走「待完成开户」。
       */
      return accountOpening.value === 'PROVISIONING' || activationDone.value === true ? 'opening' : null
    }
    return explicit
  })
  /** 正在开通（含掉单但仍在补单窗口内）——前台显示「开通中，稍后分配」 */
  const mt5Opening = computed(() => mt5Phase.value === 'opening')
  /** 开通遇到问题（掉单超窗或首开提案已取消）——文案不说「失败」 */
  const mt5Failed = computed(() => mt5Phase.value === 'failed')

  /**
   * 首登强制改密（V1.4 §5.2）。与「密码已过期」共用同一个强制改密页与 40301 闸，
   * 但页面文案要能分辨——「首次登录，请设置您的登录密码」和「密码已过期」不是一回事。
   */
  const firstLoginForced = computed(
    () => mustChangePassword.value
      && !(passwordExpireInDays.value != null && passwordExpireInDays.value <= 0),
  )
  const terminal = computed(() => isTerminal(mode.value))

  const caps = computed<Capability[]>(() => capsOf(stage.value, mode.value))
  const can = (c: Capability) => caps.value.includes(c)

  /** 点击受限入口时该弹哪个弹窗（§6.1 分流） */
  const blockKind = computed<BlockKind>(() => blockKindOf(stage.value, mode.value))

  /** 密码已过期 → 全屏强制改密页（R-18） */
  const passwordExpired = computed(
    () => mustChangePassword.value || (passwordExpireInDays.value != null && passwordExpireInDays.value <= 0),
  )

  async function fetchMe() {
    profile.value = await profileApi.me()
    /**
     * 刷新页面时 session 没了，密码剩余天数**只能从这里拿**（4.0 起 ProfileResult 有）。
     * 不同步的话刷新一次分级提醒就消失了 —— 那正是 R-18 最容易漏的一处。
     *
     * `mustChangePassword` 也在这里同步：业务接口撞 40301 时 http 钩子先拉本接口（40301 闸下放行），
     * 靠这两个字段分辨「首登强制」与「密码过期」—— 两者改密页的文案与是否要验旧密码都不同。
     * 后端没带字段（旧版本）时不动原值，不要把 undefined 写成 false 盖掉登录响应里的 true。
     */
    if (profile.value.passwordExpireInDays != null) {
      passwordExpireInDays.value = profile.value.passwordExpireInDays
    }
    if (profile.value.mustChangePassword != null) {
      mustChangePassword.value = profile.value.mustChangePassword
    }
    return profile.value
  }

  async function fetchProgress() {
    progress.value = await progressApi.get()
    return progress.value
  }

  /**
   * 登录成功后立刻用登录响应铺好门禁所需的一切。
   * **只在放行时调用**：被拒的响应里除 denyReason 外一概是 null，铺进 store 只会让门禁按空值算。
   */
  function applyLogin(r: LoginResult) {
    session.value = r
    mustChangePassword.value = r.mustChangePassword ?? false
    passwordExpireInDays.value = r.passwordExpireInDays ?? null
    pwdModalShown.value = false
    pwdBannerDismissed.value = false
    tasksModalShown.value = false
  }

  /** 通知语言改成功后同步到本地档案（接口 7.0 `POST /c/profile/locale` 返回归一后的值） */
  function applyLocale(locale: string) {
    if (profile.value) profile.value = { ...profile.value, locale }
    if (session.value) session.value = { ...session.value, locale }
  }

  /**
   * 须补填手机号（cfd-platform 决策 029，D-98）：只认 `/profile/me` 的 `phoneRequired`（登录响应没有这个字段）。
   * 刚登录时 profile 还没回来，弹窗晚一拍出现是预期的。
   */
  const phoneRequired = computed(() => profile.value?.phoneRequired === true)

  /**
   * 补填手机号成功后先在本地放下弹窗条件，权威值（E.164 的 phone）由随后的 `fetchMe` 刷新——
   * `fetchMe` 失败时弹窗也不该卡着不走：再点一次只会撞 40911「已登记」。
   */
  function applyPhoneFilled() {
    if (profile.value) profile.value = { ...profile.value, phoneRequired: false }
  }

  /**
   * 接口 40301 之后 `/profile/me` 仍没立起任何一个字段时的兜底：按「密码已过期」处理（天数记 0）。
   * **不要**在这里置 `mustChangePassword = true`——那会被改密页当成首登、藏掉「当前密码」栏，提交必 422。
   */
  function markPasswordExpired() {
    passwordExpireInDays.value = 0
  }

  /**
   * 改密成功后（§5.3.3）。
   *
   * 接口 4.0 起**本会话保留**：后端换发新 token（旧的立即失效），
   * 前端换上去就能继续用，不再"改完被踢去重登"。
   * 到期天数按后端返回的重置值（=90），横幅与弹窗立即消失、无需刷新。
   *
   * `days` 传 undefined 表示后端没给（4.0 的 YAML 200 响应仍是通用 Success，
   *）—— 那就退回原来的行为：本地按 90 天算。
   */
  function onPasswordChanged(days?: number | null) {
    if (days != null) passwordExpireInDays.value = days
    else if (passwordExpireInDays.value != null) passwordExpireInDays.value = 90
    mustChangePassword.value = false
    pwdModalShown.value = true
    pwdBannerDismissed.value = true
  }

  /**
   * 只清 auth 自己这一份状态。
   *
   * ⚠️ **不要在这里清 token / 账户 store / 查询缓存** —— 整个会话的清理归
   * `stores/session.ts` 的 `clearSession()`，这里少清一样都会漏掉一个入口。
   * 原先 `logout()` 长在这个 store 里，只清了 token 和自己，账户 store 与
   * 查询缓存没人管，于是换账号登录后上一位客户的 MT5 账号还挂在页面上。
   */
  function reset() {
    session.value = null
    profile.value = null
    progress.value = null
    passwordExpireInDays.value = null
    mustChangePassword.value = false
    pwdModalShown.value = false
    pwdBannerDismissed.value = false
    tasksModalShown.value = false
    activationDone.value = null
  }

  return {
    session, profile, progress, passwordExpireInDays, mustChangePassword,
    pwdModalShown, pwdBannerDismissed, tasksModalShown,
    ready, mode, stage, email, displayName,
    onboarded, accountOpening, provisioning, openFailed, terminal, caps, blockKind, passwordExpired,
    clientType, isWholesale, customerType, isRetailClient, mt5Reported, activationDone, mt5Phase, mt5Opening, mt5Failed, firstLoginForced,
    phoneRequired,
    can, fetchMe, fetchProgress, applyLogin, applyLocale, applyPhoneFilled, markPasswordExpired, setActivationDone,
    onPasswordChanged, reset,
  }
})
